import { AppState } from "@Store/index";
import {
  APP_SHELL,
  AppShell,
  appShellFromWindow,
  isNativeAndroidAppShell,
  isNativeIOSAppShell,
  isNewNativeAppShell,
  parseAppShell,
} from "constants/appShell";
import { useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";

declare global {
  interface Window {
    isApp?: boolean;
    platform?: string;
    /** New native shells only: `ios_native` | `android_native` */
    appShell?: string;
  }
}

const useIsApp = () => {
  const { isApp } = useSelector(
    ({ appData }: AppState) => ({
      isApp: appData.isApp,
    }),
    shallowEqual,
  );
  // The native app injects `window.isApp = true` before content loads, so read
  // it synchronously on the first client render. This avoids an effect-driven
  // false -> true flip that briefly renders the non-app UI and causes a layout
  // shift. On the server (and in a normal browser) it falls back to the SSR value.
  const [isAmberApp, setIsAmberApp] = useState(
    () => isApp || (typeof window !== "undefined" && Boolean(window.isApp)),
  );

  useEffect(() => {
    if (isApp || (typeof window !== "undefined" && window.isApp)) {
      setIsAmberApp(true);
    }
  }, [isApp]);

  return isAmberApp;
};

/** New native shell id (`ios_native` / `android_native`). Empty for RN and web. */
export const useAppShell = (): AppShell | "" => {
  const { appShell } = useSelector(
    ({ appData }: AppState) => ({
      appShell: appData.appShell,
    }),
    shallowEqual,
  );

  const [shell, setShell] = useState<AppShell | "">(
    () =>
      parseAppShell(appShell) ||
      (typeof window !== "undefined" ? parseAppShell(window.appShell) : ""),
  );

  useEffect(() => {
    const next =
      parseAppShell(appShell) ||
      (typeof window !== "undefined" ? parseAppShell(window.appShell) : "");
    // Intentional "set once": the shell is a native-host identity established at
    // startup and must persist for the webview's lifetime. Redux `appShell` can go
    // transiently empty (e.g. state reset on logout) even though we are still inside
    // the native shell, so we never clear a known shell back to "" — that would
    // incorrectly flip the app into web mode. `window.appShell` (injected by the host)
    // remains the authoritative source, so only upgrade to a truthy value.
    if (next) setShell(next);
  }, [appShell]);

  return shell;
};

export const useIsNewNativeApp = () => isNewNativeAppShell(useAppShell());

export const isAppWebViewFromAppData = (appData?: {
  isApp?: boolean;
  appShell?: string;
}): boolean => Boolean(appData?.isApp || isNewNativeAppShell(parseAppShell(appData?.appShell)));

export const useIsNativeIOSApp = () => isNativeIOSAppShell(useAppShell());
export const useIsNativeAndroidApp = () => isNativeAndroidAppShell(useAppShell());

// @ts-ignore
export const isApp = () => __CLIENT__ && (window.isApp || window?.__INITIAL_STATE__?.appData.isApp);

/**
 * Non-hook counterpart to `useIsNewNativeApp`, for class components and render-time branches
 * (`ErrorBoundary`, `NotFound`) that cannot call hooks.
 */
export const isNewNativeApp = (): boolean =>
  __CLIENT__ && isNewNativeAppShell(appShellFromWindow());
export const appOS: string = __CLIENT__ ? window.platform || "" : "";

export { APP_SHELL };
export default useIsApp;
